import { BrowserCacheLocation } from "@azure/msal-browser";

/**
 * Azure AD B2C requires `knownAuthorities` so MSAL can resolve OIDC metadata for
 * *.b2clogin.com (otherwise: ClientAuthError endpoints_resolution_error).
 * @param {string | undefined} authorityUrl e.g. https://tenant.b2clogin.com/tenant.onmicrosoft.com/policy
 * @returns {string[]}
 */
function b2cKnownAuthoritiesFromAuthority(authorityUrl) {
  if (!authorityUrl || typeof authorityUrl !== "string") {
    return [];
  }
  try {
    const host = new URL(authorityUrl.trim()).hostname;
    return host ? [host] : [];
  } catch {
    return [];
  }
}

/**
 * Ensures `offline_access` is requested so MSAL caches rotating refresh tokens in
 * localStorage and prefers the refresh-token POST path over hidden iframe renewal.
 * @param {Array<string | undefined>} scopes
 * @returns {string[]}
 */
function ensureOfflineAccessScope(scopes) {
  const list = scopes.filter(
    (scope) => typeof scope === "string" && scope.length > 0,
  );
  if (!list.includes("offline_access")) {
    list.push("offline_access");
  }
  return list;
}

/**
 * Dedicated redirect URI for silent iframe flows (`acquireTokenSilent`, `ssoSilent`).
 * Must host the MSAL v5 redirect bridge (`public/auth.html`) and be registered in B2C.
 */
export const msalSilentRedirectUri = `${window.location.origin}/auth.html`;

/**
 * MSAL browser configuration for the default sign-in / SUSI policy.
 * Used by PublicClientApplication (see msalInstance.js).
 */
export const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_AZURE_APP_CLIENT_ID,
    authority: process.env.REACT_APP_AZURE_AD_AUTHORITY,
    /** Required for B2C custom policies — fixes endpoints_resolution_error on login/signup. */
    knownAuthorities: b2cKnownAuthoritiesFromAuthority(
      process.env.REACT_APP_AZURE_AD_AUTHORITY,
    ),
    /** Interactive login / logout — full app handles `handleRedirectPromise`. */
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    /** Persist refresh tokens across tabs and browser restarts (PKCE public client). */
    cacheLocation: BrowserCacheLocation.LocalStorage,
  },
  system: {
    /**
     * Top-level SPA — do not allow interactive redirects inside hidden iframes.
     * Silent renewal uses refresh tokens first; iframe fallback targets auth.html.
     */
    allowRedirectInIframe: false,
    /** Default MSAL v5 iframe bridge timeout (hidden iframe fallback only). */
    iframeBridgeTimeout: 10_000,
  },
};

/** API / login scopes (single env value wrapped as array, matching prior react-aad-msal usage). */
export const defaultScopes = ensureOfflineAccessScope([
  process.env.REACT_APP_AZURE_APP_SCOPES,
]);

/**
 * Builds a silent token request that uses the dedicated redirect bridge page
 * (`auth.html`) so hidden iframes do not bootstrap the full React app.
 *
 * @param {import("@azure/msal-browser").AccountInfo} account
 * @returns {import("@azure/msal-browser").SilentRequest}
 */
export function buildAcquireTokenSilentRequest(account) {
  return {
    scopes: defaultScopes,
    account,
    redirectUri: msalSilentRedirectUri,
  };
}

/**
 * @param {{ authority: string, sid?: string, loginHint?: string }} params
 * @returns {import("@azure/msal-browser").SsoSilentRequest}
 */
export function buildSsoSilentRequest({ authority, sid, loginHint }) {
  return {
    scopes: defaultScopes,
    authority,
    redirectUri: msalSilentRedirectUri,
    ...(sid ? { sid } : { loginHint }),
  };
}
